import React from "react";
import { formatINR, formatDate } from "../../utils/format";

/**
 * Renders a student's installment schedule as a set of cards, each with its
 * number, amount, due date, status and (for pending ones) a Pay button.
 *
 * Generalized from the installment markup in pages/student/FeeStructure.jsx so
 * it can drive both tuition installments (installmentDetails: [{ installmentNumber,
 * amount, dueDate, status, paidDate }]) and application-fee installments
 * (pendingInstallments: [{ installmentNumber, amount, percentage, description }]).
 *
 * @param {Array}    installments   Installment rows (see shapes above)
 * @param {Function} onPay          (installmentNumber) => void
 * @param {boolean}  paymentLoading  Disables buttons while a checkout is being set up
 * @param {boolean}  canPay          Whether payments are currently accepted
 * @param {string}   [accent]        Tailwind color stem for the Pay button ("blue"|"orange"|"green")
 */
const InstallmentProgress = ({
  installments = [],
  onPay,
  paymentLoading = false,
  canPay = true,
  accent = "blue",
}) => {
  if (!installments || installments.length === 0) return null;

  const payBtnClass =
    accent === "orange"
      ? "bg-orange-600 hover:bg-orange-700"
      : accent === "green"
        ? "bg-green-600 hover:bg-green-700"
        : "bg-blue-600 hover:bg-blue-700";

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {installments.map((inst) => {
        const isPaid = inst.status === "paid";
        return (
          <div
            key={inst.installmentNumber}
            className={`rounded-lg border p-4 ${
              isPaid
                ? "bg-green-50 border-green-200"
                : "bg-white border-gray-200"
            }`}
          >
            <div className="flex justify-between items-center mb-2">
              <span className="font-medium text-gray-800">
                Installment {inst.installmentNumber}
              </span>
              {inst.status && (
                <span
                  className={`px-2 py-1 rounded-full text-xs ${
                    isPaid
                      ? "bg-green-100 text-green-800"
                      : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  {inst.status.toUpperCase()}
                </span>
              )}
            </div>

            <div className="text-sm text-gray-600">
              Amount: {formatINR(inst.amount)}
            </div>

            {inst.dueDate && (
              <div className="text-sm text-gray-600">
                Due Date: {formatDate(inst.dueDate)}
              </div>
            )}

            {(inst.description || inst.percentage) && (
              <div className="text-xs text-gray-500 mt-0.5">
                {inst.description || `${inst.percentage}% of total`}
              </div>
            )}

            {isPaid && inst.paidDate && (
              <div className="text-sm text-green-600 mt-2">
                Paid on: {formatDate(inst.paidDate)}
              </div>
            )}

            {!isPaid && canPay && onPay && (
              <button
                onClick={() => onPay(inst.installmentNumber)}
                disabled={paymentLoading}
                className={`mt-3 w-full text-white py-2 px-4 rounded-md disabled:opacity-50 ${payBtnClass}`}
              >
                {paymentLoading ? "Processing..." : "Pay Now"}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default InstallmentProgress;
